import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { leaveApi } from '../../api/leave';
import { LeaveRequest } from '../../types/leave';
import { ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { RecordDetailModal } from '../../components/RecordDetailModal';
import { ReturnAction } from '../../components/ReturnAction';
import { useToast } from '../../components/Toast';

/** Only shows requests already approved by the employee's real supervisor - the final, role-based HR confirmation step. */
export function LeaveReviewPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [viewingRequest, setViewingRequest] = useState<LeaveRequest | null>(null);
  const pendingQuery = useQuery({ queryKey: ['leave-pending-hr'], queryFn: () => leaveApi.pendingForHR() });

  const decideMutation = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: 'approved' | 'rejected' }) => leaveApi.hrDecide(id, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); queryClient.invalidateQueries({ queryKey: ['leave-pending-hr'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <h1>Leave - HR Confirmation</h1>
      <p className="muted">Requests already approved by the employee's supervisor, awaiting final HR confirmation.</p>
      {pendingQuery.data && (
        <DataTable
          rows={pendingQuery.data.data}
          rowKey={(r) => r.id}
          emptyMessage="Nothing awaiting HR confirmation."
          columns={[
            { header: 'Employee', render: (r) => r.employee_name },
            { header: 'Type', render: (r) => r.leave_type_name },
            { header: 'Dates', render: (r) => `${r.start_date} - ${r.end_date}` },
            { header: 'Days', render: (r) => r.days_requested },
            {
              header: '',
              render: (r) => (
                <span className="row-actions">
                  <button className="link-button" onClick={() => setViewingRequest(r)}>View</button>
                  <button className="link-button" onClick={() => decideMutation.mutate({ id: r.id, decision: 'approved' })}>Confirm</button>
                  <button className="link-button" onClick={() => decideMutation.mutate({ id: r.id, decision: 'rejected' })}>Reject</button>
                  <ReturnAction onReturn={(comment) => leaveApi.hrReturn(r.id, comment)} queryKeysToInvalidate={[['leave-pending-hr']]} />
                </span>
              ),
            },
          ]}
        />
      )}

      {viewingRequest && (
        <RecordDetailModal
          title={`Leave Request - ${viewingRequest.employee_name ?? ''}`}
          onClose={() => setViewingRequest(null)}
          fields={[
            { label: 'Leave Type', value: viewingRequest.leave_type_name },
            { label: 'Dates', value: `${viewingRequest.start_date} - ${viewingRequest.end_date}` },
            { label: 'Days Requested', value: viewingRequest.days_requested },
            { label: 'Supervisor Decision', value: viewingRequest.supervisor_decision },
            { label: 'Reason', value: viewingRequest.reason, fullWidth: true },
            { label: 'Return Comment', value: viewingRequest.return_comment, fullWidth: true },
          ]}
        />
      )}
    </div>
  );
}
